import { useTranslation } from "react-i18next";

import { formatDateShort } from "../lib/currency";

/**
 * Render an aggregate ratings display for a public/private profile page.
 * `summary` shape matches RatingSummaryOut from the backend.
 * `dimensions` is a list of {key, label} tuples to render as mini-bars.
 */
export default function RatingsAggregate({ summary, dimensions, emptyState }) {
  const { t } = useTranslation();
  if (!summary || summary.count === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="ratings-empty">
        {emptyState || t("ratings.empty")}
      </div>
    );
  }
  const overall = summary.overall_avg ?? 0;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="ratings-aggregate">
      <div className="flex items-baseline gap-3">
        <span className="text-3xl font-semibold text-amber-500" data-testid="ratings-overall">★ {overall.toFixed(1)}</span>
        <span className="text-sm text-slate-500">/ 5 · {t("ratings.based_on", { count: summary.count })}</span>
      </div>
      <div className="mt-4 grid gap-2">
        {dimensions.map(({ key, label }) => {
          const avg = summary[`${key}_avg`];
          if (avg === null || avg === undefined) return null;
          return (
            <div key={key} className="flex items-center gap-3 text-sm" data-testid={`ratings-dim-${key}`}>
              <span className="w-40 text-slate-600">{label}</span>
              <div className="flex-1 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-2 bg-indigo-500"
                  style={{ width: `${(avg / 5) * 100}%` }}
                />
              </div>
              <span className="w-10 text-right text-xs font-semibold text-slate-700">{avg.toFixed(1)}</span>
            </div>
          );
        })}
      </div>
      {summary.recent_comments && summary.recent_comments.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="ratings-comments">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("ratings.recent_comments")}</p>
          {summary.recent_comments.map((c, idx) => (
            <blockquote key={`${c.created_at}-${c.rater_org_name || idx}`} className="border-l-2 border-indigo-100 pl-3 text-sm text-slate-700">
              <p className="italic">“{c.comment}”</p>
              <footer className="mt-1 text-xs text-slate-500">
                <span className="font-medium">{c.rater_org_name || "—"}</span> · <span className="text-amber-500">★ {c.overall}</span> · {formatDateShort(c.created_at)}
              </footer>
            </blockquote>
          ))}
        </div>
      )}
    </div>
  );
}
